/**
 * Settings modal — 6 sections for configuring tracker preferences.
 * Changes are persisted immediately to the omen_tracker_prefs cookie via setPrefs.
 * Click outside or ✕ to close.
 */

import { useRef } from 'react';
import { useAppStore } from '../store/appStore';
import { DEFAULT_PREFS } from '../utils/cookieStore';
import { IconMapping } from '../constants/iconMapping';
import { MAP_STYLES } from '../constants/mapStyles';
import styles from './SettingsModal.module.css';

interface Props {
  /** Called when the modal should close (no pending state — all changes are live). */
  onClose: () => void;
}

const POLLING_OPTIONS: { label: string; value: number }[] = [
  { label: '1 min (défaut)', value: 60 },
  { label: '2 min', value: 120 },
  { label: '4 min', value: 240 },
  { label: '8 min', value: 480 },
];

const DATE_FILTER_OPTIONS: { label: string; value: number }[] = [
  { label: '1 mois', value: 1 },
  { label: '3 mois', value: 3 },
  { label: '6 mois', value: 6 },
  { label: '12 mois', value: 12 },
  { label: '24 mois', value: 24 },
  { label: 'Pas de limite', value: 0 },
];

/**
 * Full-screen overlay settings modal.
 * @param onClose - callback invoked when the modal should be dismissed
 */
export default function SettingsModal({ onClose }: Props) {
  const { prefs, setPrefs } = useAppStore();
  const overlayRef = useRef<HTMLDivElement>(null);
  const ResetIcon = IconMapping.settings_reset_color;

  /** Dismiss only when clicking the backdrop, not the modal card. */
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  /** Update a single color slot and persist. */
  const updateColor = (index: number, value: string) => {
    const updated = [...prefs.flightColors];
    updated[index] = value;
    setPrefs({ flightColors: updated });
  };

  /** Reset a single color slot to the default palette. */
  const resetColor = (index: number) => {
    const updated = [...prefs.flightColors];
    updated[index] = DEFAULT_PREFS.flightColors[index];
    setPrefs({ flightColors: updated });
  };

  return (
    <div className={styles.overlay} ref={overlayRef} onClick={handleOverlayClick}>
      <div className={styles.modal}>

        {/* ── Modal header ─────────────────────────────── */}
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>Paramètres</span>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            title="Fermer"
            data-tour="tour-settings-close-button"
          >
            ✕
          </button>
        </div>

        {/* ── Modal body (scrollable) ───────────────────── */}
        <div className={`${styles.modalBody} scrollable`}>

          {/* Section 1 — Couleurs des vols */}
          <section className={styles.section} data-tour="tour-settings-colors">
            <h3 className={styles.sectionTitle}>Couleurs des vols</h3>
            {prefs.flightColors.map((color, i) => (
              <div key={i} className={styles.colorRow}>
                <span className={styles.colorLabel}>Couleur {i + 1}</span>
                <input
                  type="color"
                  className={styles.colorInput}
                  value={color}
                  onChange={(e) => updateColor(i, e.target.value)}
                  title={`Couleur du vol ${i + 1}`}
                />
                <span className={styles.colorHex}>{color.toUpperCase()}</span>
                <button
                  className={styles.resetColorBtn}
                  onClick={() => resetColor(i)}
                  title="Réinitialiser la couleur"
                >
                  <ResetIcon size={13} />
                </button>
              </div>
            ))}
          </section>

          {/* Section 2 — Prédictions par défaut par vol */}
          <section className={styles.section} data-tour="tour-settings-predictions">
            <h3 className={styles.sectionTitle}>Prédictions (par défaut par vol)</h3>
            <p className={styles.sectionNote}>
              Appliqué aux nouveaux vols ajoutés à la sélection.
            </p>
            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={prefs.defaultShowNormal}
                onChange={(e) => setPrefs({ defaultShowNormal: e.target.checked })}
              />
              <span>Prédiction normale</span>
            </label>
            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={prefs.defaultShowInstant}
                onChange={(e) => setPrefs({ defaultShowInstant: e.target.checked })}
              />
              <span>Prédiction instantanée</span>
            </label>
          </section>

          {/* Section 3 — Affichage global des prédictions */}
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Affichage des prédictions</h3>
            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={prefs.globalShowHeatmap}
                onChange={(e) => setPrefs({ globalShowHeatmap: e.target.checked })}
              />
              <span>Afficher la <em>heatmap</em> des prédictions d'atterrissage</span>
            </label>
            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={prefs.globalShowPolygon}
                onChange={(e) => setPrefs({ globalShowPolygon: e.target.checked })}
              />
              <span>Afficher la zone de prédictions d'atterrissage</span>
            </label>
          </section>

          {/* Section 4 — Fond de carte par défaut (2 options) */}
          <section className={styles.section} data-tour="tour-settings-basemap">
            <h3 className={styles.sectionTitle}>Fond de carte par défaut</h3>
            {(['street', 'satellite'] as const).map((key) => (
              <label key={key} className={styles.radioRow}>
                <input
                  type="radio"
                  name="mapStyle"
                  value={key}
                  checked={prefs.defaultMapStyle === key}
                  onChange={() => setPrefs({ defaultMapStyle: key })}
                />
                <span>{MAP_STYLES[key].label}</span>
              </label>
            ))}
          </section>

          {/* Section 5 — Intervalle de mise à jour */}
          <section className={styles.section} data-tour="tour-settings-update-interval">
            <h3 className={styles.sectionTitle}>Intervalle de mise à jour</h3>
            <div className={styles.radioGrid}>
              {POLLING_OPTIONS.map((opt) => (
                <label key={opt.value} className={styles.radioRow}>
                  <input
                    type="radio"
                    name="pollingInterval"
                    value={opt.value}
                    checked={prefs.pollingIntervalSec === opt.value}
                    onChange={() => setPrefs({ pollingIntervalSec: opt.value })}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Section 6 — Filtre par date */}
          <section className={styles.section} data-tour="tour-settings-flight-filter">
            <h3 className={styles.sectionTitle}>Filtre des vols par date</h3>
            <div className={styles.selectRow}>
              <span className={styles.selectLabel}>Afficher les vols des :</span>
              <select
                className={styles.dateSelect}
                value={prefs.flightDateFilterMonths}
                onChange={(e) => setPrefs({ flightDateFilterMonths: Number(e.target.value) })}
              >
                {DATE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {/* Sections 7+8 wrapped together for the tour ui-scale step */}
          <div data-tour="tour-settings-ui-scale">
            {/* Section 7 — Taille de police */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Taille du texte</h3>
              <div className={styles.sliderRow}>
                <span style={{ fontSize: '0.75rem', color: '#888' }}>A</span>
                <input
                  type="range"
                  className={styles.slider}
                  min={12}
                  max={20}
                  step={1}
                  value={prefs.fontSize}
                  onChange={(e) => setPrefs({ fontSize: Number(e.target.value) })}
                />
                <span style={{ fontSize: '1.125rem', color: '#888' }}>A</span>
                <span className={styles.sliderValue}>{prefs.fontSize} px</span>
              </div>
            </section>

            {/* Section 8 — Largeur du panneau de vol */}
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Largeur du panneau de vol</h3>
              <div className={styles.sliderRow}>
                <input
                  type="range"
                  className={styles.slider}
                  min={300}
                  max={450}
                  step={10}
                  value={prefs.panelWidth}
                  onChange={(e) => setPrefs({ panelWidth: Number(e.target.value) })}
                />
                <span className={styles.sliderValue}>{prefs.panelWidth} px</span>
              </div>
            </section>
          </div>

        </div>
      </div>
    </div>
  );
}
